const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const API_KEY = import.meta.env.VITE_PONZINOMICS_API_KEY || 'test_dev_key_12345';

// Mock mode: use mock data when backend unavailable
export const MOCK_MODE = import.meta.env.VITE_MOCK_API === 'true' ||
  (import.meta.env.DEV && !import.meta.env.VITE_API_URL);

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

class FeatureApiClient {
  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      ...(options.headers as Record<string, string> || {}),
    };

    const body = options.body ? JSON.stringify(options.body) : null;
    const config: RequestInit = {
      ...options,
      headers,
      body,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `Request failed with status ${response.status}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }

  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Upload a File as base64-encoded JSON payload.
   * Converts the file to base64, sends as { image, contentType }.
   */
  async uploadImage(endpoint: string, file: File): Promise<{ url: string }> {
    const buffer = await file.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );

    return this.request<{ url: string }>(endpoint, {
      method: 'POST',
      body: {
        image: base64,
        contentType: file.type,
      },
    });
  }
}

export const featureApi = new FeatureApiClient();
